import { Autocomplete, Grid, InputLabel, TextField, useTheme } from '@mui/material';
import { useCustomTheme } from '../../store/theme-context';

type Props = {
	id: string;
	label: string;
	validated: boolean;
	errorMsg: string;
	value: string | string[];
	values: any;
	valueChangeHandler: (e: any, value: any) => void;
	labelSx?: any;
	multiple?: boolean;
	multipleMax?: number;
	/** Pozwala wpisać wartość spoza `values` (np. afiliacja "uczelnia" nieobecna w katalogu szkół). */
	freeSolo?: boolean;
	helperText?: string;
};

const FormComboBox = ({
	id,
	label,
	validated,
	errorMsg,
	valueChangeHandler,
	value,
	values,
	labelSx,
	multiple,
	multipleMax,
	freeSolo,
	helperText,
}: Props) => {
	const { typography, palette } = useTheme();
	const { isHighContrast } = useCustomTheme();

	return (
		<Grid
			container
			flexDirection={'column'}
			width={'100%'}
			gap={'4px'}
		>
			<InputLabel
				htmlFor={id}
				sx={{
					fontSize: typography.subtitle2.fontSize,
					fontWeight: '500',
					color: palette.primary.main,
					...labelSx,
				}}
			>
				{label}
			</InputLabel>
			<Autocomplete
				fullWidth
				disablePortal
				id={id}
				options={values}
				value={multiple ? (Array.isArray(value) ? value : []) : value || null}
				disableClearable={!freeSolo}
				freeSolo={freeSolo}
				multiple={multiple}
				disableCloseOnSelect={!!multiple}
				getOptionDisabled={() => {
					if (!multiple || !multipleMax) return false;
					const selectedCount = Array.isArray(value) ? value.length : 0;
					return selectedCount >= multipleMax;
				}}
				noOptionsText={'Nie znaleziono'}
				size='small'
				isOptionEqualToValue={(option, selected) => option === selected}
				renderInput={(params) => (
					<TextField
						{...params}
						label=''
						error={validated && errorMsg !== ''}
						helperText={validated && errorMsg ? errorMsg : helperText || undefined}
					/>
				)}
				onChange={(e: any, newValue: any) => {
					if (multiple) {
						valueChangeHandler(e, newValue);
						return;
					}
					const selected = typeof newValue === 'string' ? newValue : '';
					valueChangeHandler(e, selected);
				}}
				onInputChange={
					freeSolo
						? (e: any, newInputValue: string, reason: string) => {
								if (reason === 'input') {
									valueChangeHandler(e, newInputValue);
								}
							}
						: undefined
				}
				sx={{
					...(multiple
						? { minHeight: '50px' }
						: {
								height: '50px',
								'& fieldset': {
									height: '50px',
								},
								'& div:first-of-type': {
									fontSize: '20px',
									display: 'flex',
									alignItems: 'center',
									height: '95%',
								},
							}),
					'& *': {
						color: palette.primary.main,
					},
					'&:hover fieldset': {
						borderColor: isHighContrast ? palette.primary.main + '!important' : palette.primary.main,
					},
					'& div': {
						py: '0',
						my: '0',
						top: '0',
					},
					'& .MuiOutlinedInput-root': {
						'& fieldset': {
							borderRadius: '4px',
							borderColor: palette.primary.main,
						},
					},
				}}
			/>
		</Grid>
	);
};

export default FormComboBox;
